import { Link } from "react-router-dom";
import { Search, Menu, User } from "lucide-react";
import baseiyLogo from "@/assets/baseiy-logo.png";
import { Button } from "@/components/ui/button";
import { CartDrawer } from "@/components/CartDrawer";
import { SimpleSearch } from "@/components/SimpleSearch";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
} from "@/components/ui/sheet";
import { useState } from "react";
import { useLanguageStore, isRTL } from "@/stores/languageStore";
import { useAuth } from "@/hooks/useAuth";

export const Header = () => {
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const { t, language } = useLanguageStore();
  const { isAuthenticated, loading } = useAuth();

  const smartNightstandLabel =
    language === "he" ? "דף נחיתה: שידת לילה חכמה" : "Smart Nightstand Landing";

  return (
    <>
      {/* Promotional Banner */}
      <div className="w-full bg-gradient-to-r from-charcoal via-charcoal/95 to-charcoal py-2.5 text-center border-b border-gold/20">
        <p className="text-sm font-medium text-gold tracking-wide">
          {t('header.promoBanner')}
        </p>
      </div>
      <header className="sticky top-0 z-50 w-full border-b border-primary/20 bg-charcoal">
      <div className="container flex h-16 items-center justify-between relative z-10">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-1">
          <img src={baseiyLogo} alt="BASE IY logo" className="h-14 w-auto" />
        </Link>

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          <Link 
            to="/" 
            className="text-sm font-medium text-white/80 transition-colors hover:text-white"
          >
            {t('nav.home')}
          </Link>
          <Link 
            to="/categories" 
            className="text-sm font-medium text-white/80 transition-colors hover:text-white"
          >
            {t('nav.categories')}
          </Link>
          <Link 
            to="/products" 
            className="text-sm font-medium text-white/80 transition-colors hover:text-white"
          >
            {t('nav.products')}
          </Link>
          <Link
            to="/lp/smart-nightstand"
            className="text-sm font-medium text-white/80 transition-colors hover:text-white"
            aria-label={smartNightstandLabel}
          >
            {language === "he" ? "שידת לילה חכמה" : "Smart Nightstand"}
          </Link>
          <Link 
            to="/about" 
            className="text-sm font-medium text-white/80 transition-colors hover:text-white"
          >
            {t('nav.about')}
          </Link>
          <Link 
            to="/contact" 
            className="text-sm font-medium text-white/80 transition-colors hover:text-white"
          >
            {t('nav.contact')}
          </Link>
        </nav>

        {/* Actions */}
        <div className="flex items-center gap-2">
          {/* Desktop Search */}
          <div className="hidden md:block">
            <SimpleSearch />
          </div>

          {/* Smart Nightstand Landing CTA (desktop) */}
          <Link to="/lp/smart-nightstand" className="hidden lg:block">
            <Button
              size="sm"
              className="gradient-gold shadow-elegant text-primary-foreground"
              aria-label={smartNightstandLabel}
            >
              {language === "he" ? "למבצע שידה חכמה" : "View Deal"}
            </Button>
          </Link>

          {/* Mobile Search Toggle */}
          <Button 
            variant="ghost" 
            size="icon"
            className="md:hidden text-white hover:bg-white/20 border border-white/30 hover:border-white/50"
            onClick={() => setMobileSearchOpen(!mobileSearchOpen)}
            aria-label="Search"
          >
            <Search className="h-5 w-5" />
          </Button>

          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Auth Button */}
          {!loading && (
            <Link to={isAuthenticated ? "/profile" : "/auth"} aria-label={isAuthenticated ? "View profile" : "Login"}>
              <Button variant="ghost" size="icon" className="hidden md:flex text-white hover:bg-white/20 border border-white/30 hover:border-white/50" aria-label={isAuthenticated ? "View profile" : "Login"}>
                <User className="h-5 w-5" />
              </Button>
            </Link>
          )}

          {/* Cart */}
          <CartDrawer />

          {/* Mobile Menu */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden text-white hover:bg-white/20 border border-white/30 hover:border-white/50" aria-label="Open menu">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side={isRTL(language) ? "right" : "left"} className="w-72">
              <nav className="flex flex-col gap-4 mt-8">
                <Link 
                  to="/" 
                  className="text-lg font-medium transition-colors hover:text-primary"
                >
                  {t('nav.home')}
                </Link>
                <Link 
                  to="/categories" 
                  className="text-lg font-medium transition-colors hover:text-primary"
                >
                  {t('nav.categories')}
                </Link>
                <Link 
                  to="/products" 
                  className="text-lg font-medium transition-colors hover:text-primary"
                >
                  {t('nav.products')}
                </Link>
                <Link
                  to="/lp/smart-nightstand"
                  className="text-lg font-medium transition-colors hover:text-primary"
                  aria-label={smartNightstandLabel}
                >
                  {language === "he" ? "שידת לילה חכמה" : "Smart Nightstand"}
                </Link>
                <Link 
                  to="/about" 
                  className="text-lg font-medium transition-colors hover:text-primary"
                >
                  {t('nav.about')}
                </Link>
                <Link 
                  to="/contact" 
                  className="text-lg font-medium transition-colors hover:text-primary"
                >
                  {t('nav.contact')}
                </Link>
                <Link 
                  to={isAuthenticated ? "/profile" : "/auth"} 
                  className="text-lg font-medium transition-colors hover:text-primary"
                >
                  {isAuthenticated ? t('nav.profile') : t('nav.login')}
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
        </div>
      </div>

      {/* Mobile Search Bar */}
      {mobileSearchOpen && (
        <div className="md:hidden border-t border-border p-3 animate-fade-in">
          <SimpleSearch onClose={() => setMobileSearchOpen(false)} />
        </div>
      )}
    </header>
    </>
  );
};
